import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import z from "zod";
import { toast } from "sonner";

import { useServer } from "@/components/server-hooks";
import type { Sources } from "@/components/source-bits";
import { ago, Chip, errorMessage, Page } from "@/components/ui-bits";
import { useAgentQuery } from "@/lib/act";
import type { RunsList } from "@/lib/runs";
import { client, orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_auth/servers/")({
  validateSearch: z.object({ error: z.string().optional() }),
  component: Servers,
});

type ServerRow = NonNullable<Awaited<ReturnType<typeof client.servers.list>>>[number];

/** Hetzner location codes the panel offers, by city (falls back to the code). */
const CITY: Record<string, string> = {
  nbg1: "Nuremberg",
  fsn1: "Falkenstein",
  hel1: "Helsinki",
  ash: "Ashburn",
  hil: "Hillsboro",
  sin: "Singapore",
};

function Servers() {
  const { error } = Route.useSearch();
  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);
  const servers = useQuery({ ...orpc.servers.list.queryOptions(), refetchInterval: 30_000 });
  const [connecting, setConnecting] = useState(false);

  return (
    <Page
      title="Servers"
      subtitle="Each server runs Fieldwork in your own cloud account."
      actions={
        <>
          <Button variant="outline" aria-expanded={connecting} onClick={() => setConnecting((v) => !v)}>
            Connect an existing machine
          </Button>
          <Link to="/servers/new">
            <Button>New server</Button>
          </Link>
        </>
      }
    >
      {connecting && <ConnectExisting onDone={() => servers.refetch()} />}
      {servers.error && <p className="m-0 text-sm text-[#F48A80]">{servers.error.message}</p>}
      {servers.data?.length === 0 && (
        <p className="m-0 rounded-md bg-[#1A1A19] px-[22px] py-5 text-[#A8A59D] shadow-[inset_0_0_0_1px_#2A2927]">
          No servers yet. Create one in your Hetzner account, or connect a machine that already runs Fieldwork.
        </p>
      )}
      {!!servers.data?.length && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(300px,100%),1fr))] gap-4">
          {servers.data.map((s) => (
            <ServerCard key={s.id} s={s} />
          ))}
        </div>
      )}
    </Page>
  );
}

function Tag({ children, hot }: { children: ReactNode; hot?: boolean }) {
  return <Chip value={hot ? "hot" : "neutral"} label={String(children)} />;
}

function ServerCard({ s }: { s: ServerRow }) {
  const online = s.status === "online";
  const manual = s.provider === "manual";
  const live = useServer(s.id);
  const install = online ? live.data?.hub.status?.install : undefined;
  const installing = !!install && install.state !== "installed" && install.state !== "unknown";
  const ready = online && !!live.data?.hub.connected && !installing;
  const proposals = useAgentQuery<{ pending: unknown[] }>(s.id, "proposals_list", {}, ready);
  const questions = useAgentQuery<{ open: unknown[]; pending: unknown[] }>(s.id, "questions_list", {}, ready);
  const sources = useAgentQuery<Sources>(s.id, "sources_status", {}, ready);
  const runs = useAgentQuery<RunsList>(s.id, "runs_list", {}, ready);

  const where = manual
    ? ["Your own machine", s.hostname]
    : [`Hetzner ${s.serverType ?? ""}`.trim(), s.location ? (CITY[s.location] ?? s.location) : null, s.ipv4];

  const waitingToConnect = manual && s.status === "booting";
  const enrolOpen = !!s.enrolExpiresAt && new Date(s.enrolExpiresAt).getTime() > Date.now();
  const [tone, label] = waitingToConnect
    ? ["info", "waiting to connect"]
    : s.status === "booting"
      ? ["booting", "starting"]
      : installing
        ? install.state === "failed"
          ? ["failed", "install failed"]
          : ["installing", "installing"]
        : [s.status, s.status];

  const nSuggestions = proposals.data?.pending.length;
  const nQuestions = questions.data ? Math.max(0, questions.data.open.length - questions.data.pending.length) : undefined;
  const st = sources.data;
  const nSources = st
    ? st.gmail.accounts.length +
      st.slack.accounts.length +
      st.notion.accounts.length +
      (st.github.configured ? 1 : 0) +
      (st.telegram.configured ? 1 : 0)
    : undefined;
  const lastCycle = runs.data?.runs[0]?.started_at;

  let body: ReactNode = null;
  let footer: string;
  if (s.status === "error") {
    body = <span className="text-[13px] text-[#F48A80]">{s.error ?? "Something went wrong. Open it for details."}</span>;
    footer = `created ${ago(s.createdAt)}`;
  } else if (waitingToConnect) {
    body = (
      <span className="text-[13px] text-[#CFCBC1]">
        {enrolOpen ? "Run its install command on the machine." : "Its install command has expired."}
      </span>
    );
    footer = `added ${ago(s.createdAt)}`;
  } else if (s.status === "creating" || s.status === "booting") {
    body = (
      <span className="text-[13px] text-[#CFCBC1]">
        {s.status === "creating"
          ? "Creating it in your Hetzner project."
          : "Starting; it connects to this panel, then installs Fieldwork and Plane."}
      </span>
    );
    footer = `created ${ago(s.createdAt)}`;
  } else if (installing) {
    body = (
      <span className={install.state === "failed" ? "text-[13px] text-[#F48A80]" : "text-[13px] text-[#CFCBC1]"}>
        {install.step || (install.state === "failed" ? "The install stopped." : "Installing Fieldwork and Plane.")}
      </span>
    );
    footer = `created ${ago(s.createdAt)} · seen ${ago(s.lastSeenAt)}`;
  } else {
    const tags = [
      nSuggestions !== undefined && (
        <Tag key="s" hot={nSuggestions > 0}>
          {`${nSuggestions} suggestion${nSuggestions === 1 ? "" : "s"}`}
        </Tag>
      ),
      nQuestions !== undefined && <Tag key="q">{`${nQuestions} question${nQuestions === 1 ? "" : "s"}`}</Tag>,
      nSources !== undefined && <Tag key="o">{`${nSources} source${nSources === 1 ? "" : "s"}`}</Tag>,
    ].filter(Boolean);
    body = tags.length > 0 ? <span className="flex flex-wrap gap-1.5">{tags}</span> : null;
    footer = [lastCycle && `last cycle ${ago(lastCycle)}`, s.status === "online" ? "online" : `seen ${ago(s.lastSeenAt)}`]
      .filter(Boolean)
      .join(" · ");
  }

  return (
    <Link
      to="/servers/$id"
      params={{ id: s.id }}
      className="flex flex-col gap-3.5 rounded-md bg-[#1A1A19] px-[22px] py-5 text-[#ECEAE4] no-underline shadow-[inset_0_0_0_1px_#2A2927] hover:shadow-[inset_0_0_0_1px_#3A3935]"
    >
      <span className="flex items-center justify-between gap-3">
        <span className="min-w-0 truncate font-mono text-[17px] font-bold">{s.name}</span>
        <Chip value={tone} label={label} />
      </span>
      <span className="text-[13px] text-[#A8A59D]">{where.filter(Boolean).join(" · ")}</span>
      {body}
      <span className="mt-auto border-t border-[#2A2927] pt-3 text-[13px] text-[#A8A59D]">{footer}</span>
    </Link>
  );
}

function ConnectExisting({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [result, setResult] = useState<{ command: string; expiresAt: number } | null>(null);
  const create = useMutation({
    mutationFn: () => client.servers.connectExisting({ name }),
    onSuccess: (r) => {
      setResult(r);
      onDone();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <section
      aria-labelledby="cx-title"
      className="flex flex-col gap-3 rounded-md bg-[#1A1A19] px-[22px] py-5 shadow-[inset_0_0_0_1px_#3A3935]"
    >
      <h2 id="cx-title" className="m-0 font-mono text-[15px] font-bold">
        {result ? `Connect “${name}”` : "Connect an existing machine"}
      </h2>
      {!result ? (
        <>
          <p className="m-0 text-[#CFCBC1]">
            For a machine that already runs Fieldwork. You get a one-line command that installs only the Fieldwork
            agent, as the user who owns the install; it connects out and opens no ports.
          </p>
          <form
            className="flex flex-wrap gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <Input
              className="min-w-0 flex-[1_1_240px] bg-[#111111]"
              aria-label="Name"
              placeholder="A name for it, e.g. home-server"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Get install command"}
            </Button>
          </form>
        </>
      ) : (
        <>
          <p className="m-0 text-[#CFCBC1]">
            Run this on the machine as the user who owns the install. It works once, until{" "}
            {new Date(result.expiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}. It installs only
            the Fieldwork agent, which connects out and opens no ports.
          </p>
          <div className="flex items-stretch gap-2">
            <pre className="m-0 min-w-0 flex-1 overflow-x-auto rounded-[4px] bg-[#0E0E0D] px-3.5 py-3 font-mono text-[13px] text-[#CFCBC1]">
              {result.command}
            </pre>
            <button
              type="button"
              className="min-w-20 cursor-pointer rounded-[4px] border-0 bg-[#2A2927] font-mono text-[13px] text-[#ECEAE4] hover:bg-[#34332F]"
              onClick={() => navigator.clipboard.writeText(result.command).then(() => toast.success("Copied"))}
            >
              Copy
            </button>
          </div>
        </>
      )}
    </section>
  );
}
